import { useCallback } from "react";
import type { DividersSetByHand } from "@CommonTypes/backend/PspRules";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { useApi } from "src/api";
import type { SetShelfDividersByHand } from "./ShelfDividerOverride";

/**
 * Saves a shelf's divider hand override on the store's fixture row, then shows
 * it on the open pog. Undefined without a store (base pogs have no fixture row).
 */
export const useSetShelfDividersByHand = ({
  merchflowId,
  storeCode,
  setPog,
}: {
  merchflowId?: number;
  storeCode?: string;
  /** Takes an updater, so pog edits made while the PUT is in flight are kept. */
  setPog: (update: (pog: Pog) => Pog) => void;
}): SetShelfDividersByHand | undefined => {
  const { putInputsMasterFixturesByStoreShelfApi } = useApi();

  const save = useCallback(
    async (shelf: PlanogramShelf, dividersSetByHand: DividersSetByHand | null) => {
      if (merchflowId === undefined || !storeCode) return false;
      const bayNo = shelf.bay.getNo();
      const shelfNo = shelf.getNo();
      const response = await putInputsMasterFixturesByStoreShelfApi({
        merchflowId,
        storeCode,
        bayNo,
        shelfNo,
        dividersSetByHand,
      });
      if (!response) return false;

      setPog((current) => {
        const next = new Pog(current.toNonCircular());
        const nextShelf = next.planogram.bays[bayNo - 1]?.shelves[shelfNo - 1];
        if (nextShelf) nextShelf.dividersSetByHand = dividersSetByHand;
        return next;
      });
      return true;
    },
    [merchflowId, storeCode, setPog, putInputsMasterFixturesByStoreShelfApi],
  );

  return merchflowId !== undefined && storeCode ? save : undefined;
};
